import { useRef, useState } from 'react'
import { ImageIcon, Loader2, Upload, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import {
  ACCEPTED_IMAGE_TYPES,
  checkImage,
  useUploadImage,
  type UploadPurpose,
} from '@/lib/uploads'

interface ImageUploadProps {
  /** The stored image URL, or '' when none is set. */
  value: string
  onChange: (url: string) => void
  purpose: UploadPurpose
  label?: string
  /** Tailwind aspect class for the thumbnail, e.g. `aspect-[3/1]` for a banner. */
  aspectClassName?: string
}

/**
 * Shared by Promo Banner and Trending News. An image can be uploaded or supplied
 * as an existing URL (spec Requirements 5 and 6), so both paths are offered
 * side by side rather than behind a toggle.
 */
export function ImageUpload({
  value,
  onChange,
  purpose,
  label = 'Image',
  aspectClassName = 'aspect-[3/1]',
}: ImageUploadProps) {
  const fileInput = useRef<HTMLInputElement>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const [brokenUrl, setBrokenUrl] = useState(false)
  const upload = useUploadImage()

  function handleFile(file: File | undefined) {
    if (!file) return

    // Checked here for an instant answer; the server checks the bytes again.
    const reason = checkImage(file)
    if (reason) {
      setLocalError(reason)
      return
    }

    setLocalError(null)
    upload.mutate(
      { file, purpose },
      {
        onSuccess: (url) => {
          setBrokenUrl(false)
          onChange(url)
        },
      },
    )
  }

  const error = localError ?? (upload.error ? (upload.error as Error).message : null)

  return (
    <div className="space-y-2">
      <Label>{label}</Label>

      <div
        className={cn(
          'relative flex items-center justify-center overflow-hidden rounded-lg border border-dashed border-border-strong bg-secondary',
          aspectClassName,
        )}
      >
        {value && !brokenUrl ? (
          <>
            <img
              src={value}
              alt=""
              className="size-full object-cover"
              onError={() => setBrokenUrl(true)}
            />
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="absolute right-2 top-2"
              aria-label="Remove image"
              onClick={() => {
                onChange('')
                setBrokenUrl(false)
                setLocalError(null)
              }}
            >
              <X />
            </Button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1 text-muted-foreground">
            <ImageIcon className="size-6" />
            <span className="text-xs">
              {brokenUrl ? 'That URL did not load' : 'No image yet'}
            </span>
          </div>
        )}

        {upload.isPending && (
          <div className="absolute inset-0 flex items-center justify-center bg-card/70">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES.join(',')}
          className="hidden"
          onChange={(event) => {
            handleFile(event.target.files?.[0])
            // Lets the same file be chosen again after a failed attempt.
            event.target.value = ''
          }}
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={upload.isPending}
          onClick={() => fileInput.current?.click()}
        >
          <Upload />
          {upload.isPending ? 'Uploading…' : 'Upload image'}
        </Button>
        <span className="text-xs text-faint">or paste a URL</span>
        <Input
          value={value}
          placeholder="https://…"
          className="h-7 max-w-xs text-xs"
          aria-label="Image URL"
          onChange={(event) => {
            setBrokenUrl(false)
            setLocalError(null)
            onChange(event.target.value)
          }}
        />
      </div>

      <p className={cn('text-xs', error ? 'text-destructive' : 'text-faint')}>
        {error ?? 'JPEG, PNG, or WebP. Up to 4 MB.'}
      </p>
    </div>
  )
}
